import { useEffect, useRef, useState } from "react";
import { Bold, Italic, Link2, List, ListOrdered, Quote, Heading2, Heading3, ImageIcon, X } from "lucide-react";
import DOMPurify from "dompurify";
import { mediaUrl } from "../lib/api";
import MediaPicker from "./MediaPicker";

const ALLOWED = ["p", "h2", "h3", "h4", "strong", "em", "u", "s", "a", "ul", "ol", "li", "blockquote", "img", "figure", "figcaption", "br", "hr", "span", "div"];

export default function RichEditor({ value, onChange, placeholder = "" }) {
  const ref = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [linkMode, setLinkMode] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value && document.activeElement !== ref.current) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    sync();
  };

  const sync = () => {
    onChange && onChange(ref.current ? ref.current.innerHTML : "");
  };

  const insertHtml = (html) => {
    ref.current?.focus();
    document.execCommand("insertHTML", false, html);
    sync();
  };

  const onPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    if (html) {
      insertHtml(DOMPurify.sanitize(html, { ALLOWED_TAGS: ALLOWED, ALLOWED_ATTR: ["href", "src", "alt", "title", "class"], ALLOWED_DATA_ATTR: false }));
    } else {
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    }
  };

  const btn = (label, title, onClick, active = false, testid) => (
    <button
      type="button"
      data-testid={testid}
      title={title}
      aria-label={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`w-9 h-9 rounded-md flex items-center justify-center text-[15px] transition-colors ${active ? "bg-enji/10 text-enji" : "text-sub hover:text-ink hover:bg-surface-muted"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="border border-line rounded-lg bg-surface overflow-hidden" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-line bg-surface-muted/50">
        {btn(<Heading2 size={16} />, "Judul bagian (H2)", () => exec("formatBlock", "<h2>"), false, "editor-h2-button")}
        {btn(<Heading3 size={16} />, "Sub bagian (H3)", () => exec("formatBlock", "<h3>"), false, "editor-h3-button")}
        {btn(<Bold size={16} />, "Tebal", () => exec("bold"), false, "editor-bold-button")}
        {btn(<Italic size={16} />, "Miring", () => exec("italic"), false, "editor-italic-button")}
        {btn(<Link2 size={16} />, "Tautan", () => setLinkMode(!linkMode), linkMode, "editor-link-button")}
        {btn(<List size={16} />, "Daftar", () => exec("insertUnorderedList"), false, "editor-ul-button")}
        {btn(<ListOrdered size={16} />, "Daftar bernomor", () => exec("insertOrderedList"), false, "editor-ol-button")}
        {btn(<Quote size={16} />, "Kutipan", () => exec("formatBlock", "<blockquote>"), false, "editor-quote-button")}
        {btn(<ImageIcon size={16} />, "Sisipkan gambar", () => setPickerOpen(true), false, "editor-image-button")}
        {btn("Teks", "Hapus format", () => exec("removeFormat"), false, "editor-clear-button")}
      </div>

      {linkMode && (
        <div className="flex gap-2 p-3 border-b border-line bg-surface-muted/30">
          <input
            data-testid="editor-link-input"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://tautan.com"
            className="field-input !min-h-[40px] flex-1"
          />
          <button type="button" data-testid="editor-link-apply" className="btn-primary !min-h-[40px] !px-4" onClick={() => { if (linkUrl.trim()) exec("createLink", linkUrl.trim()); setLinkUrl(""); setLinkMode(false); }}>
            Terapkan
          </button>
          <button type="button" aria-label="Batal" className="btn-ghost !min-h-[40px] !px-3" onClick={() => { exec("unlink"); setLinkMode(false); }}>
            <X size={15} />
          </button>
        </div>
      )}

      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Isi konten"
        data-testid="rich-editor-area"
        className="reader p-5 min-h-[280px] focus:outline-none max-w-none"
        style={{ "--reader-size": "16px" }}
        onInput={sync}
        onBlur={sync}
        onPaste={onPaste}
        data-placeholder={placeholder}
      />

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(m) => {
          setPickerOpen(false);
          insertHtml(
            `<figure><img src="${mediaUrl(m.id)}" alt="${(m.alt || "").replace(/"/g, "&quot;")}" loading="lazy"/>${m.caption ? `<figcaption>${m.caption}</figcaption>` : ""}</figure><p><br/></p>`
          );
        }}
      />
    </div>
  );
}

